/**
 * DailyBriefing — 2026-06-21
 *
 * One friendly sign-in pop-up that replaces the pile of separate
 * interruptions (welcome/tour overlay + announcement catch-up + notif nags).
 * Shows ONCE PER DAY on first sign-in: greets by name and gives the three
 * things that matter — tasks today, announcements you missed, unread
 * messages — each with a one-tap deep-link. Rows with a zero count hide, so
 * it never feels noisy. Dismissing WITHOUT catching up leaves announcements
 * unread (badge stays) per user's choice.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CheckSquare, Megaphone, ChatCircleDots, ArrowRight, X as XIcon, Cake } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

// Store-local (Denver) YYYY-MM-DD so "once per day" flips at store midnight.
function storeToday() {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Denver", year: "numeric", month: "2-digit", day: "2-digit",
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export default function DailyBriefing({ user, birthday, onGoBirthday, onGoTasks, onGoAnnouncements, onGoInbox }) {
  const [state, setState] = useState(null); // {tasks, announcements, unread} once loaded
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const crewId = user?.crew_id;
    if (!crewId) return undefined;
    const key = `wow:briefing:${crewId}`;
    let seen = "";
    try { seen = localStorage.getItem(key) || ""; } catch { /* ignore */ }
    const today = storeToday();
    if (seen === today) return undefined; // already briefed today

    const date = today;
    const storeId = user.home_store_id || "";
    (async () => {
      const [tasksR, annR, unreadR] = await Promise.allSettled([
        axios.get(`${API}/zone-sheets/my/${date}`, { params: { actor_id: crewId, store_id: storeId } }),
        axios.get(`${API}/announcements/unseen`, { params: { crew_id: crewId, store_id: storeId } }),
        axios.get(`${API}/inbox/${crewId}/unread-count`, { params: { is_manager: !!user.is_manager } }),
      ]);
      if (cancelled) return;
      const tasks = (tasksR.status === "fulfilled" ? (tasksR.value.data?.tasks || []) : [])
        .filter((t) => !["complete", "manager_verified", "done", "verified"].includes(t.status));
      const announcements = annR.status === "fulfilled" ? (annR.value.data?.count || 0) : 0;
      const unread = unreadR.status === "fulfilled" ? (unreadR.value.data?.unread || 0) : 0;
      setState({ tasks: tasks.length, announcements, unread });
      setOpen(true);
      // Mark briefed for today — showing the counts IS the catch-up; we do
      // NOT mark announcements read here (they stay unread until opened).
      try { localStorage.setItem(key, today); } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user?.crew_id, user?.home_store_id, user?.is_manager]);

  if (!open || !state) return null;

  const { tasks, announcements, unread } = state;
  const allClear = tasks === 0 && announcements === 0 && unread === 0 && !birthday;
  const name = user?.first_name ? `, ${user.first_name}` : "";

  const rows = [
    birthday && {
      key: "bday", Icon: Cake, tint: "pink",
      label: birthday.is_honoree
        ? (birthday.kind === "anniversary"
            ? `🏆 It's your ${birthday.years}-year anniversary! See your card`
            : "🎂 It's your birthday! See your card")
        : (birthday.kind === "anniversary"
            ? `🏆 ${birthday.first_name}'s ${birthday.years}-year anniversary today`
            : `🎂 It's ${birthday.first_name}'s birthday today`),
      cta: birthday.is_honoree ? "Open my card" : "Sign the card",
      onClick: () => { setOpen(false); onGoBirthday?.(); },
    },
    tasks > 0 && {
      key: "tasks", Icon: CheckSquare, tint: "emerald",
      label: `${tasks} task${tasks === 1 ? "" : "s"} for you today`,
      cta: "Go to tasks", onClick: () => { setOpen(false); onGoTasks?.(); },
    },
    announcements > 0 && {
      key: "ann", Icon: Megaphone, tint: "indigo",
      label: `${announcements} announcement${announcements === 1 ? "" : "s"} you missed`,
      cta: "Catch up", onClick: () => { setOpen(false); onGoAnnouncements?.(); },
    },
    unread > 0 && {
      key: "inbox", Icon: ChatCircleDots, tint: "sky",
      label: `${unread} unread message${unread === 1 ? "" : "s"}`,
      cta: "Open inbox", onClick: () => { setOpen(false); onGoInbox?.(); },
    },
  ].filter(Boolean);

  const TINT = {
    emerald: "bg-emerald-100 text-emerald-700",
    indigo: "bg-indigo-100 text-indigo-700",
    sky: "bg-sky-100 text-sky-700",
    pink: "bg-pink-100 text-pink-700",
  };

  return (
    <div
      className="fixed inset-0 z-[9997] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-testid="daily-briefing"
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-[briefingIn_320ms_cubic-bezier(0.16,1,0.3,1)]"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="px-5 pt-6 pb-5 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white relative">
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/15"
            data-testid="daily-briefing-close"
            aria-label="Dismiss"
          >
            <XIcon weight="bold" className="w-5 h-5" />
          </button>
          <p className="text-[11px] font-black uppercase tracking-widest text-indigo-300">{greeting()}</p>
          <p className="text-2xl font-black leading-tight mt-0.5">What&apos;s up{name} 👋</p>
          <p className="text-[13px] text-slate-300 mt-1">
            {allClear ? "You're all caught up — have a great shift." : "Here's your quick rundown for today."}
          </p>
        </div>

        {!allClear && (
          <div className="p-3 space-y-2">
            {rows.map((r) => (
              <button
                key={r.key}
                onClick={r.onClick}
                className="w-full flex items-center gap-3 rounded-2xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 px-3.5 py-3 text-left transition-colors group"
                data-testid={`daily-briefing-${r.key}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${TINT[r.tint]}`}>
                  <r.Icon weight="fill" className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-bold text-slate-900 leading-tight">{r.label}</p>
                  <p className="text-[12px] font-semibold text-slate-400">{r.cta}</p>
                </div>
                <ArrowRight weight="bold" className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all" />
              </button>
            ))}
          </div>
        )}

        <div className="p-4 pt-2">
          <button
            onClick={() => setOpen(false)}
            className="w-full rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black text-sm py-3.5"
            data-testid="daily-briefing-dismiss"
          >
            {allClear ? "Let's go" : "Got it"}
          </button>
        </div>
      </div>
      <style>{`@keyframes briefingIn { from { opacity: 0; transform: translateY(16px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
    </div>
  );
}
